<template>
  <div>
    <!-- Table -->
    <PremiumTable
      title="Super Admins"
      subtitle="Administration access control for the platform."
      :headers="headers"
      :items="admins"
      :loading="loading"
      show-search
      search-placeholder="Search admins..."
      show-add
      add-label="Add Admin"
      @add="openAdd"
    >
      <template v-slot:item.profile_url="{ item }">
        <PremiumAvatar
          :image="item.profile_url ? API_BASE_URL + '/uploads/super_admins/profile_pics/' + item.profile_url : null"
          :name="item.name"
          :size="32"
        />
      </template>

      <template v-slot:item.mobile_display="{ item }">
        <span style="white-space:nowrap;font-size:13.5px;">{{ item.mobile_display }}</span>
      </template>

      <template v-slot:item.created_at="{ item }">
        <span style="font-size:13px;color:#64748b;white-space:nowrap;">{{ formatDate(item.created_at) }}</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <div style="display:flex;gap:8px;align-items:center;">
          <PremiumActionButton
            icon="mdi-pencil-outline"
            label="Edit"
            @click="openEdit(item)"
          />
          <PremiumActionButton
            v-if="item.id !== currentUser.id"
            icon="mdi-delete-outline"
            label="Delete"
            variant="danger"
            @click="openDelete(item.id)"
          />
        </div>
      </template>
    </PremiumTable>

    <!-- Add/Edit Modal -->
    <PremiumModalForm
      v-model="adminDialog"
      :title="isEdit ? 'Edit Super Admin' : 'Create New Super Admin'"
      :submitText="isEdit ? 'Save Changes' : 'Create Super Admin'"
      :loading="saving"
      width="500"
      @submit="saveAdmin"
    >
      <div class="ism-modal-body">
        <!-- Avatar Upload Section -->
        <div class="ism-avatar-upload">
          <div class="avatar-preview-container">
            <input type="file" ref="admin_photo" accept="image/*" @change="handleFile" hidden>
            <div class="avatar-circle">
              <PremiumAvatar
                :image="previewUrl || (isEdit && editItem.profile_url ? API_BASE_URL + '/uploads/super_admins/profile_pics/' + editItem.profile_url : null)"
                :name="editItem.name || 'Super Admin'"
                :size="72"
              />
            </div>
            <button type="button" class="avatar-edit-trigger" @click="$refs.admin_photo.click()" title="Change Photo">
              <v-icon size="16" color="#fff">mdi-camera-outline</v-icon>
            </button>
          </div>
          <div class="avatar-upload-info">
            <div class="info-title">Profile Picture</div>
            <div class="info-subtitle">JPG, PNG up to 2MB</div>
          </div>
        </div>

        <div class="ism-form-group">
          <PremiumInput
            v-model="editItem.name"
            name="name"
            label="Full Name *"
            placeholder="e.g. John Doe"
            v-validate="'required'"
            :error="errors.first('name') || serverSideErrors.name"
          />

          <PremiumInput
            v-model="editItem.email"
            name="email"
            label="Email Address *"
            placeholder="admin@ismartsociety.com"
            v-validate="'required|email'"
            :error="errors.first('email') || serverSideErrors.email"
          />

          <div class="ism-mobile-field">
            <label class="premium-label">Mobile Number *</label>
            <div class="ism-mobile-row" :class="{ 'has-mobile-error': errors.has('mobile_no') }">
              <div class="ism-mobile-code">
                <div class="ism-country-selector-wrap">
                  <country-code-select
                    v-model="countryIso"
                    @onSelect="onCountrySelect"
                  />
                </div>
              </div>
              <div class="ism-mobile-number">
                <PremiumInput
                  v-model="editItem.mobile_no"
                  name="mobile_no"
                  placeholder="9876543210"
                  v-validate="'required|numeric|min:8'"
                  :error="errors.first('mobile_no') || serverSideErrors.mobile_no"
                  data-vv-as="Mobile Number"
                />
              </div>
            </div>
          </div>

          <div v-if="!isEdit" class="ism-password-alert">
            <v-icon size="18" color="#F26522">mdi-shield-key-outline</v-icon>
            <div class="alert-content">
              A 6-digit secure password will be auto-generated and sent to the administrator's email.
            </div>
          </div>
        </div>
      </div>
    </PremiumModalForm>

    <!-- Delete Confirm Modal -->
    <ConfirmModal
      :isOpen="deleteDialog"
      type="danger"
      title="Remove Admin"
      message="Are you sure you want to remove this super admin?"
      confirmText="Yes, Remove"
      cancelText="Cancel"
      @confirm="deleteAdmin"
      @cancel="deleteDialog = false"
    />

    <!-- Image Cropper -->
    <ImageCropper
      v-model="cropperOpen"
      :image-src="rawImageSrc"
      title="Crop Admin Photo"
      shape="circle"
      :output-size="400"
      @cropped="onPhotoCropped"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import moment from 'moment';
import PremiumTable from '../common/PremiumTable.vue';
import ConfirmModal from '../common/ConfirmModal.vue';
import PremiumModalForm from '../common/PremiumModalForm.vue';
import PremiumInput from '../common/PremiumInput.vue';
import PremiumActionButton from '../common/PremiumActionButton.vue';
import PremiumAvatar from '../common/PremiumAvatar.vue';
import ImageCropper from '../common/ImageCropper.vue';

export default {
  name: 'SuperAdminsPage',
  components: { PremiumTable, PremiumModalForm, PremiumInput, ConfirmModal, PremiumActionButton, PremiumAvatar, ImageCropper },

  data() {
    return {
      loading: false,
      saving: false,
      admins: [],
      adminDialog: false,
      deleteDialog: false,
      isEdit: false,
      edit_id: null,
      delete_id: null,
      currentUser: JSON.parse(localStorage.getItem('user') || '{}'),
      
      countryIso: 'in',
      photoFile: null,
      previewUrl: null,
      cropperOpen: false,
      rawImageSrc: '',
      API_BASE_URL: process.env.VUE_APP_API_URL || 'https://api-staging.ismartsociety.com/api',

      editItem: {
        name: '',
        email: '',
        mobile_no: '',
        country_code: '+91',
        countryIso: 'in',
        profile_url: null
      },
      
      serverSideErrors: {},

      headers: [
        { text: 'Photo',         value: 'profile_url', width: '60px', sortable: false },
        { text: 'Name',          value: 'name',       width: '25%' },
        { text: 'Email ID',      value: 'email',      width: '25%' },
        { text: 'Mobile No.',    value: 'mobile_display', width: '20%' },
        { text: 'Date Added',    value: 'created_at', width: '15%' },
        { text: 'Actions',       value: 'actions',    width: '15%', sortable: false },
      ],
    };
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),


    onCountrySelect(e) {
      this.editItem.country_code = '+' + e.dialCode;
      this.editItem.countryIso = e.iso2;
    },

    handleFile(e) {
      const file = e.target.files[0];
      if (!file) return;
      e.target.value = '';
      const reader = new FileReader();
      reader.onload = ev => {
        this.rawImageSrc = ev.target.result;
        this.cropperOpen = true;
      };
      reader.readAsDataURL(file);
    },

    onPhotoCropped({ file, previewUrl }) {
      this.photoFile  = file;
      this.previewUrl = previewUrl;
    },

    getAdmins() {
      this.loading = true;
      this.$axios.get('/superadmin/super-admins').then(res => {
        this.loading = false;
        if (res.data.status) {
          this.admins = res.data.admins.map(a => ({
            ...a,
            mobile_display: (a.country_code ? (a.country_code.startsWith('+') ? a.country_code : '+' + a.country_code) : '') + ' ' + (a.mobile_no || '')
          }));
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
        }
      }).catch(() => { this.loading = false; });
    },

    openAdd() {
      this.isEdit = false;
      this.editItem = { name: '', email: '', mobile_no: '', country_code: '+91', countryIso: 'in', profile_url: null };
      this.countryIso = 'in';
      this.photoFile = null;
      this.previewUrl = null;
      this.serverSideErrors = {};
      this.$validator.reset();
      this.adminDialog = true;
    },

    openEdit(item) {
      this.isEdit = true;
      this.edit_id = item.id;
      this.editItem = JSON.parse(JSON.stringify(item));
      this.countryIso = item.countryIso || 'in';
      this.photoFile = null;
      this.previewUrl = null;
      this.serverSideErrors = {};
      this.$validator.reset();
      this.adminDialog = true;
      console.log('Editing Admin ID:', this.edit_id);
    },

    saveAdmin() {
      this.$validator.validateAll().then(valid => {
        if (!valid) return;
        
        this.saving = true;
        this.serverSideErrors = {};
        
        let formData = new FormData();
        formData.append('name', this.editItem.name);
        formData.append('email', this.editItem.email);
        formData.append('mobile_no', this.editItem.mobile_no);
        formData.append('country_code', this.editItem.country_code);
        formData.append('countryIso', this.editItem.countryIso);
        
        if (this.photoFile) {
          formData.append('profile_pic', this.photoFile);
        }
        
        const endpoint = this.isEdit ? `/superadmin/super-admins/${this.edit_id}?_method=PUT` : '/superadmin/super-admins';
        
        // Since we are using FormData, we should use POST and maybe append _method for PUT if Adonis doesn't handle natively
        const method = 'post';
        if (this.isEdit) formData.append('_method', 'PUT');
        
        this.$axios[method](endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => {
          this.saving = false;
          const data = res.data;
          
          if (data.status) {
            this.getAdmins();
            this.adminDialog = false;
            this.setAlert({ 
              showAlert: true, 
              variant: 'green accent-4', 
              timeout: 3500, 
              message: this.isEdit ? 'Admin updated successfully.' : 'Admin created and welcome email sent.' 
            });
          } else if (data.errors) {
            data.errors.forEach(e => {
              this.$set(this.serverSideErrors, e.field, e.message);
            });
            this.setAlert({ 
              showAlert: true, 
              variant: 'red accent-3', 
              timeout: 4000, 
              message: 'Correct the errors in the form before proceeding.' 
            });
          } else {
            this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: data.message || 'Something went wrong' });
          }
        }).catch(() => { this.saving = false; });
      });
    },

    openDelete(id) {
      console.log('Opening delete for ID:', id);
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteAdmin() {
      const id = this.delete_id;
      if (!id) {
        this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: 'Invalid Admin ID. Please refresh the page.' });
        this.deleteDialog = false;
        return;
      }
      this.deleteDialog = false;
      this.$axios.delete(`/superadmin/super-admins/` + id).then(res => {
        const data = res.data;
        if (data.status) {
          this.admins = this.admins.filter(a => a.id !== id);
          this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: data.message });
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: data.message });
        }
      });
    },

    formatDate(date) {
      return moment(date).format('DD MMM, YYYY');
    }
  },

  mounted() {
    this.$validator.localize('en', {
      custom: {
        mobile_no: {
          required: 'Mobile number is required',
          numeric: 'Please enter a valid mobile number',
          min: 'Mobile number must be at least 8 digits'
        },
        email: {
          required: 'Email address is required',
          email: 'Please enter a valid email address'
        },
        name: {
          required: 'Full name is required'
        }
      }
    });
    this.getAdmins();
  },
};
</script>

<style scoped>
.ism-modal-body { padding: 8px 4px; }

/* ── Avatar Upload ────────────────────────── */
.ism-avatar-upload {
  display: flex; align-items: center; gap: 20px;
  margin-bottom: 24px; padding-bottom: 24px;
  border-bottom: 1px solid var(--border-color);
}
.avatar-preview-container { position: relative; flex-shrink: 0; }
.avatar-circle {
  width: 72px; height: 72px; border-radius: 50%;
  background: var(--bg-primary); border: 2px solid var(--border-color);
  overflow: hidden; display: flex; align-items: center; justify-content: center;
  position: relative; box-shadow: var(--shadow-sm);
}
.avatar-circle img { width: 100%; height: 100%; object-fit: cover; }

.avatar-placeholder-premium {
  width: 100%; height: 100%;
  background: linear-gradient(135deg, var(--accent), var(--accent-hover));
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 24px; font-weight: 800;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  line-height: 1; text-transform: uppercase;
}

.avatar-edit-trigger {
  position: absolute;
  bottom: 0px;
  right: 0px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--accent);
  border: 2px solid var(--card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 2;
  box-shadow: var(--shadow-sm);
}
.avatar-edit-trigger:hover { background: var(--accent-hover); transform: scale(1.1); }

.avatar-upload-info { display: flex; flex-direction: column; gap: 2px; }
.info-title { font-size: 14px; font-weight: 700; color: var(--text-primary); }
.info-subtitle { font-size: 11px; color: var(--text-secondary); }

/* ── Form Layout ───────────────────────────── */
.ism-form-group { display: flex; flex-direction: column; gap: 4px; }

.ism-mobile-field { margin-bottom: 20px; }
.ism-mobile-row { display: flex; gap: 12px; align-items: flex-start; }
.ism-mobile-code { width: 80px; padding-top: 0; }
.ism-mobile-number { flex: 1; }
.ism-mobile-number >>> .premium-input-wrapper { margin-bottom: 0; }
.ism-mobile-number >>> .v-input { margin-top: 0 !important; padding-top: 0 !important; }
.ism-mobile-number >>> .v-input__control { min-height: 40px !important; }

/* Sync borders on error */
.has-mobile-error .ism-country-selector-wrap {
  border-color: #ef4444 !important;
}
.has-mobile-error .ism-mobile-number >>> fieldset {
  border-color: #ef4444 !important;
}

/* Shift error message to the left to align with mobile field start */
.ism-mobile-number >>> .v-text-field__details {
  margin-left: -92px !important;
  width: calc(100% + 92px) !important;
  padding-left: 0 !important;
  padding-top: 6px !important;
  display: block !important;
}
.ism-mobile-number >>> .v-messages__message {
  text-align: left !important;
}

.premium-label {
  display: block; font-size: 13px; font-weight: 600;
  color: var(--text-secondary); margin-bottom: 8px;
  font-family: "Inter", sans-serif;
  white-space: nowrap;
}

.ism-country-selector-wrap {
  height: 40px; border: 1px solid var(--border-color2);
  border-radius: 8px; background: var(--bg-primary) !important;
  display: flex; align-items: center; 
  transition: all 0.2s;
  position: relative;
  overflow: visible;
}
.ism-country-selector-wrap:hover,
.ism-country-selector-wrap:focus-within {
  border-color: var(--accent) !important;
}

.ism-country-selector-wrap >>> .dropdown:hover {
  background-color: transparent !important;
}

/* Custom styles for the library component to force alignment and theme */
.ism-country-selector-wrap >>> .country-code-select,
.ism-country-selector-wrap >>> .vue-country-select,
.ism-country-selector-wrap >>> .selected-flag,
.ism-country-selector-wrap >>> .dropdown-button,
.ism-country-selector-wrap >>> .dropdown-toggle {
  border: none !important; 
  width: 100% !important; 
  height: 100% !important;
  background: transparent !important; 
  background-color: transparent !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 14px !important; 
  color: var(--text-primary) !important;
  box-shadow: none !important;
}

.ism-country-selector-wrap >>> .vue-country-select {
  border: none !important; 
  width: 100% !important;
  background: transparent !important;
}

/* Dropdown — smart positioning, dark mode bg, wider */
.ism-country-selector-wrap >>> .dropdown-list,
.ism-country-selector-wrap >>> ul {
  background-color: var(--card-bg, #fff) !important;
  border: 1px solid var(--border-color2, rgba(0,0,0,0.12)) !important;
  color: var(--text-primary) !important;
  z-index: 99999 !important;
  min-width: 280px !important;
  max-height: 260px !important;
  overflow-y: auto !important;
  padding: 6px 0 !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18) !important;
  border-radius: 10px !important;
  /* Smart direction: default below, JS will flip if needed */
  position: absolute !important;
  left: 0 !important;
  top: calc(100% + 4px);
  bottom: auto; 
}

/* Optional: if you specifically want to force it above when needed, 
   you'd usually need a class like .drop-up. 
   But we'll ensure it has a good max-height to avoid cutoff. */

/* Dark mode dropdown background */
body.dark-mode .ism-country-selector-wrap >>> .dropdown-list,
body.dark-mode .ism-country-selector-wrap >>> ul,
.theme--dark .ism-country-selector-wrap >>> .dropdown-list,
.theme--dark .ism-country-selector-wrap >>> ul {
  background-color: #1e293b !important;
  border-color: rgba(255,255,255,0.1) !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
}

body.dark-mode .ism-country-selector-wrap >>> .dropdown-item,
body.dark-mode .ism-country-selector-wrap >>> li,
.theme--dark .ism-country-selector-wrap >>> .dropdown-item,
.theme--dark .ism-country-selector-wrap >>> li {
  color: #f1f5f9 !important;
}

body.dark-mode .ism-country-selector-wrap >>> .dropdown-item:hover,
body.dark-mode .ism-country-selector-wrap >>> li:hover,
.theme--dark .ism-country-selector-wrap >>> .dropdown-item:hover,
.theme--dark .ism-country-selector-wrap >>> li:hover {
  background-color: rgba(255,255,255,0.06) !important;
}

.ism-country-selector-wrap >>> .dropdown-list::-webkit-scrollbar,
.ism-country-selector-wrap >>> ul::-webkit-scrollbar {
  width: 5px;
}
.ism-country-selector-wrap >>> .dropdown-list::-webkit-scrollbar-thumb,
.ism-country-selector-wrap >>> ul::-webkit-scrollbar-thumb {
  background: var(--border-color2);
  border-radius: 10px;
}

.ism-country-selector-wrap >>> .dropdown-item,
.ism-country-selector-wrap >>> li {
  background-color: transparent !important;
  color: var(--text-primary) !important;
  padding: 9px 14px !important;
  font-size: 13px !important;
  font-family: "Inter", sans-serif !important;
  transition: background 0.15s !important;
}

.ism-country-selector-wrap >>> .dropdown-item:hover,
.ism-country-selector-wrap >>> li:hover {
  background-color: var(--bg-primary) !important;
  color: var(--accent) !important;
}

.ism-country-selector-wrap >>> .dropdown-item.active,
.ism-country-selector-wrap >>> li.active {
  background-color: rgba(242, 101, 34, 0.1) !important;
  color: #F26522 !important;
}

/* ── Alerts ────────────────────────────────── */
.ism-password-alert {
  display: flex; gap: 12px; padding: 14px 16px;
  background: rgba(242, 101, 34, 0.05);
  border: 1px solid rgba(242, 101, 34, 0.1);
  border-radius: 12px; margin-top: 12px;
}
.alert-content {
  font-size: 12px; font-weight: 500; line-height: 1.5;
  color: var(--text-secondary);
}

@media (max-width: 600px) {
  .ism-mobile-row { flex-direction: column; gap: 4px; }
  .ism-code-box { width: 100%; }
}
</style>
